import { type Ref, createApp, type App, defineComponent, ref, computed } from 'vue-demi'
import { type ExperienceActivationPayload } from '../../../../../../types/models'
import { type IVueActivationTargetRenderer } from './activation-target-renderer'
import { ActivationPreviewListGrid } from '@source-digital/activation-list-vue'
import { BaseVueActivationTargetRenderer } from './base'
import { type ExtractProps } from '../../../../../../types/helper'
import { applyInlineStyles } from '../../../../../../utils/dom-style'
import { setupOverlay } from '../../../../../../utils/overlay'
import { type IDetailsOverlayTarget } from './details-overlay-target'
import { type UseElementBoundingReturn } from '@vueuse/core'
import { useElementBoundingPatch } from '../utils/patches/use-element-bounding-patch'
import { useActivationPreviewListGrid } from '../utils/use-activation-preview-list-grid'

export class VueActivationTargetOverlayRenderer extends BaseVueActivationTargetRenderer implements IVueActivationTargetRenderer, IDetailsOverlayTarget {
  itemsMap: Ref<Record<string, ExperienceActivationPayload[] | undefined>>
  renderTargetEl: HTMLElement
  vueApp: App
  vueInstance: Vue
  #overlayWatch: () => void
  #areaElBounding: Record<string, UseElementBoundingReturn> = {}
  #gridElRef: Ref<InstanceType<typeof ActivationPreviewListGrid> | undefined>
  additionalStyle: Ref<Record<string, any>>
  wrapperEl: HTMLElement
  constructor (itemsMap: Ref<Record<string, ExperienceActivationPayload[] | undefined>>, ...args: ConstructorParameters<typeof BaseVueActivationTargetRenderer>) {
    super(...args)
    this.itemsMap = itemsMap
    this.additionalStyle = ref({})
  }

  getElementBounding (name?: string): UseElementBoundingReturn | undefined {
    if (name == null) return undefined
    if (this.#areaElBounding[name] == null) {
      this.#areaElBounding[name] = useElementBoundingPatch(() => this.#gridElRef.value?.findElementByArea(name) as HTMLElement ?? undefined)
    }
    return this.#areaElBounding[name]
  }

  async initialize () {
    const wrapperEl = this.createElement('div', undefined, { role: 'overlay' })
    applyInlineStyles({ 'pointer-events': 'none', ...(this.ctx.settings?.direct?.options?.overlayContainerStyle ?? {}) }, wrapperEl)
    this.renderTargetEl = this.createElement('div')
    wrapperEl.appendChild(this.renderTargetEl)
    this.wrapperEl = wrapperEl
    // use offset for positioning if outer-overlay is enabled.
    this.#overlayWatch = setupOverlay(this.ctx.targetEl, wrapperEl, { useOffset: this.directSettings.layout?.initOptions?.outerOverlay?.enabled === true })
    // setup float
    this.vueApp = createApp(this.#createComponent())
    this.ctx.targetEl.after(wrapperEl)
    this.vueInstance = this.vueApp.mount(this.renderTargetEl)
  }

  applyStyleToWrapper (style: Record<string, any>) {
    applyInlineStyles(style, this.wrapperEl)
  }

  #createComponent () {
    this.#gridElRef = ref()
    const itemsMap = computed(() => this.detailsRenderer.isOpen.value ? {} : this.itemsMap.value)
    const attrs = this.ctx.getComponentMarkerAttributes('overlay')
    const {
      getListStyle,
      getOptions
    } = useActivationPreviewListGrid(this.currentLayout, 'overlay')
    return defineComponent({
      name: 'EmbedderActivationTargetOverlay',
      provide: this.createProvideObject(),
      attrs,
      setup: () => ({
        gridEl: this.#gridElRef
      }),
      render: h => {
        if (this.hide.value) return h()
        return h(ActivationPreviewListGrid, {
          ref: 'gridEl',
          class: this.rootClass,
          props: {
            grid: this.currentLayout.value?.targets?.overlay?.grid,
            itemsMap: itemsMap.value,
            animation: this.currentLayout.value?.targets?.overlay?.animation ?? this.directSettings.options?.animation,
            listStyle: getListStyle,
            options: getOptions
          } satisfies ExtractProps<typeof ActivationPreviewListGrid>,
          style: {
            width: '100%',
            height: this.ctx.embedTarget?.bottomSpace != null ? `calc(100% - ${this.ctx.embedTarget?.bottomSpace})` : '100%',
            overflow: 'hidden',
            ...this.additionalStyle.value,
            ...(this.currentLayout.value?.targets?.overlay?.style ?? {})
          },
          on: {
            ...this.createDefaultListeners()
          }
        })
      }
    })
  }

  destroy () {
    if (this.vueApp != null) this.vueApp?.unmount?.()
    this.#overlayWatch?.()
  }
}
